import cn from "classnames";
import { FormEvent, useEffect, useState, useRef } from "react";

// components
import { TrashOutlineIcon } from "components/icons";
import {
  RoundedButton,
  Input,
  Button,
} from "components/LibraryComponents/Components";
import SBACertificateCard from "components/BusinessProfileSections/ProfileCertifications/SBACertificateCard";
import { Saved } from "components/EditBusinessProfileSections/shared/Saved/Saved";
import { ResponsiveStack } from "components/ResponsiveStack/ResponsiveStack";

// utils
import { uuidv4, formatNonFedCertPayload } from "utils/edit-profile";
import { saveErrorToast } from "utils/edit-profile";

// types
import { SectionsProps } from "pages/BusinessProfileEdit/BusinessProfileEdit";

// hooks 
import { useMeiliTaskToast } from "hooks/useMeiliTaskToast";
import { useSaveConfirmation } from "hooks/useSaveConfirmation";

// store
import { useProfileEditStore } from "store/useProfileEditStore";

// styles
import styles from "./CertificationsSection.module.scss";

import { updateExtendedProfileData } from "data/fetch";

export type CertificationsFormData = {
  id: string;
  name: string;
  isNew?: boolean; // used to identify unsaved items
};

const MAX_LIMIT = 75;

function getCurrentSBACerts(certs) {
  return certs.filter((x) => x.active);
}

function getPastSBACerts(certs) {
  return certs.filter((x) => !x.active && !x?.suspended);
}

function getSuspendedSBACerts(certs) {
  return certs.filter((x) => x.suspended);
}

export default function CertificationsSection({
  setUnsavedChanges,
  businessProfileData,
}: SectionsProps) {
  const { extendedProfile, setExtendedProfile } = useProfileEditStore(
    (state) => ({
      extendedProfile: state.extendedProfile,
      setExtendedProfile: state.setExtendedProfile,
    })
  );

  const [formData, setFormData] = useState<CertificationsFormData[]>([]);
  const initialDataRef = useRef<CertificationsFormData[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [pendingAddAnother, setPendingAddAnother] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const certs = businessProfileData?.certs ?? [];
  const isEmpty = formData.length === 0;
  const showAddMore = formData.length < MAX_LIMIT;
  const isAnyEmpty = formData.some((item) => item.name === "");
  const formRef = useRef<HTMLFormElement>(null);

  const currentCertificates = getCurrentSBACerts(certs);
  const previousCertificates = getPastSBACerts(certs);
  const suspendedCertificates = getSuspendedSBACerts(certs);

  const NON_FEDERAL_GOVT_CERTIFICATIONS = "Non-federal government certifications";

  const { 
    lastSavedSection,
    showConfirmation,
    clearConfirmation
  } = useSaveConfirmation<typeof NON_FEDERAL_GOVT_CERTIFICATIONS>();

  const addTask = useMeiliTaskToast()

  const hasAnyCertificationChanged = () =>
    JSON.stringify(formData) !== JSON.stringify(initialDataRef.current);

  const parseCertifications = (
    value: string | null | undefined
  ): CertificationsFormData[] =>
    value
      ? value.split(";").map((name) => ({
          id: uuidv4(),
          name: name.trim() || "",
        }))
      : [];

  useEffect(() => {
    const initialCertifications = parseCertifications(
      extendedProfile?.non_fed_govt_certifications
    );
    setFormData(initialCertifications);
    initialDataRef.current = [...initialCertifications];
  }, [extendedProfile]);

  useEffect(() => {
    setUnsavedChanges(hasAnyCertificationChanged());
    return () => {
      setUnsavedChanges(false);
    };
  }, [formData, setUnsavedChanges]);

  // Add another non-federal certification only after saving
  useEffect(() => {
    if (pendingAddAnother && !isSaving) {
      handleCreate();
      setPendingAddAnother(false);
    }
  }, [pendingAddAnother, isSaving]);

  const handleCreate = () => {
    if (formData.length < MAX_LIMIT && (
      formData.length === 0 || formRef.current?.reportValidity())
    ) {
      setFormData((prev) => [
        ...prev,
        {
          id: uuidv4(),
          name: "",
          isNew: true // allow easily removing unsaved items
        },
      ]);
    }
  };

  const onChange = (data: CertificationsFormData) => {
    clearConfirmation()
    const updateFormData = formData.map((item) => {
      if (item.id === data.id) {
        return data;
      } else {
        return item;
      }
    });

    setFormData(updateFormData);
  };

  const onDelete = async (id: string) => {
    const itemToDelete = formData.find((item) => item.id === id);

    if (!id || !itemToDelete) return;

    if (itemToDelete.isNew) {
      // If it's a new item, just remove it from the list
      setFormData((prev) => prev.filter((item) => item.id !== id));
      return;
    }

    const updateFormData = formatNonFedCertPayload(formData.filter((item) => item.id !== id))

    setDeletingId(id);

    try {
      const response = await updateExtendedProfileData(
        extendedProfile.uei,
        JSON.stringify({
          non_fed_govt_certifications: updateFormData
        }))

        if (response.ok) {
          setExtendedProfile({
            ...extendedProfile,
            non_fed_govt_certifications: updateFormData
          });

          setFormData(updateFormData);
          initialDataRef.current = [...updateFormData];

          const responseData = await response.json();
          addTask(
            {
              taskUid: responseData?.taskUid,
              label: NON_FEDERAL_GOVT_CERTIFICATIONS,
              status: responseData?.status,
            }
          )
          showConfirmation(NON_FEDERAL_GOVT_CERTIFICATIONS);
        }

    } catch (error: any) {
      console.error(`Error deleting certification:`, error)
      saveErrorToast()
    } finally {
      setDeletingId(null);
    }
  }

  const handleSaveCertifications = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const form = e.target as HTMLFormElement
    const submitter = (e.nativeEvent as any).submitter
    const isAddAnother = submitter?.name === "addAnother"

    if (isAddAnother && !hasAnyCertificationChanged()) {
      handleCreate();
      return;
    }

    if (!form.reportValidity()) {
      console.log("form is invalid")
      return
    }

    if (!hasAnyCertificationChanged() || isSaving) return

    setIsSaving(true);
    try {
      const certificationsPayload = formatNonFedCertPayload(formData)

      const response = await updateExtendedProfileData(
        extendedProfile.uei,
        JSON.stringify({
          non_fed_govt_certifications: certificationsPayload,
        })
      );

      if (response.ok) {
        setExtendedProfile({
          ...extendedProfile,
          non_fed_govt_certifications: certificationsPayload,
        });

        initialDataRef.current = [...formData.map((item) => ({ ...item, isNew: false }))];

        const responseData = await response.json();
        addTask(
          {
            taskUid: responseData?.taskUid,
            label: NON_FEDERAL_GOVT_CERTIFICATIONS,
            status: responseData?.status,
          }
        );
        showConfirmation(NON_FEDERAL_GOVT_CERTIFICATIONS);
      }
    } catch (error: any) {
      console.error(`Error saving Non-federal government certifications:`, error)
      saveErrorToast();
    } finally {
      setIsSaving(false);
      if (isAddAnother) {
        setPendingAddAnother(true);
      }
    }
  };

  return (
    <div className={styles["certifications-section-root"]}>
      <form onSubmit={handleSaveCertifications} ref={formRef}>
        <h2 className={styles["heading"]}>Certifications</h2>
        <div className={cn(styles["section-content"], styles["space-y-6"])}>
          <div>
            <h3 className={styles["title"]}>SBA Certifications</h3>
            {!certs || certs?.length === 0 ? (
              <p className={styles["desc"]}>
                This small business doesn’t have any SBA certifications. Visit{" "}
                <a
                  href="https://certifications.sba.gov"
                  target="_blank"
                  rel="noreferrer"
                >
                  certifications.SBA.gov
                </a>{" "}
                to learn more about the certification process.
              </p>
            ) : (
              <>
                <p className={styles["desc"]}>
                  This is the SBA certification information visible on your
                  business prpfile page. Visit{" "}
                  <a
                    href="https://certifications.sba.gov"
                    target="_blank"
                    rel="noreferrer"
                  >
                    certifications.SBA.gov
                  </a>{" "}
                  for more detailed information on your SBA certifications.
                </p>

                <div className={styles["list-content"]}>
                  <div className={styles["sba-certs-list"]}>
                    {currentCertificates.map((cert, index) => {
                      return <SBACertificateCard key={index} {...cert} />;
                    })}

                    {suspendedCertificates.map((cert, index) => {
                      return <SBACertificateCard key={index} {...cert} />;
                    })}

                    {previousCertificates.map((cert, index) => {
                      return <SBACertificateCard key={index} {...cert} />;
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className={cn(styles["section-content"], styles["space-y-6"])}>
          <div>
            <h3 className={styles["title"]}>
              Non-federal government certifications
            </h3>
            <p className={styles["desc"]}>
              Non-federal government certifications include state certifications,
              such as State Certified Native American, or industry-related
              competence certifications, such as Certified Public Accountant,
              Licensed Electrician, Registered Nurse, etc.
            </p>
          </div>

          <div className={styles["space-y-4"]}>
            {!isEmpty &&
              formData.map((item) => (
                <CertificationsForm
                  key={item.id}
                  id={item.id}
                  data={item}
                  onChange={onChange}
                  onDelete={onDelete}
                  isSaving={isSaving}
                  deletingId={deletingId}
                />
              ))}

            {isEmpty && (
              <p className={styles["empty-text"]}>
                No non-federal government certifications currently listed.
              </p>
            )}

            {showAddMore && (
              <Button
                type="submit"
                name="addAnother"
                className={styles["outline-btn"]}
                disabled={isAnyEmpty || isSaving || deletingId !== null}
                loading={isSaving}
              >
                {isEmpty
                  ? "Add a non-federal government certification" :
                  hasAnyCertificationChanged() ? "Save and add another" : "Add another"
                }

              </Button>
            )}
          </div>

          {!showAddMore && (
            <p className={styles["nfgc-limit-text"]}>
              <strong>
                You have entered the maximum number of non-federal government
                certifications.
              </strong>{" "}
              Change your certification list above, or remove existing ones before
              adding more.
            </p>
          )}

          <ResponsiveStack>
            <Button
              type="submit"
              loading={isSaving}
              disabled={
                isEmpty || isAnyEmpty || isSaving || !hasAnyCertificationChanged() || deletingId !== null
              }
            >
              {isSaving ? "Saving..." : "Save changes"}
            </Button>
            {lastSavedSection === NON_FEDERAL_GOVT_CERTIFICATIONS && <Saved />}
          </ResponsiveStack>
        </div>
      </form>
    </div>
  );
}

type CertificationsFormProps = {
  id: string;
  data: CertificationsFormData;
  onChange: (data: CertificationsFormData) => void;
  onDelete: (id: string) => void;
  deletingId: string | null;
  isSaving: boolean;
};

function CertificationsForm(props: CertificationsFormProps) {
  const { id, data, onChange, onDelete, isSaving, deletingId } = props;

  return (
    <div className={cn(styles["certifications-form"], styles["space-y-4"])}>
      <div className={styles["space-y-2"]}>
        <div className={styles["header"]}>
          <h4>Certification name</h4>

          <RoundedButton
            type="button"
            variant="ghost"
            className={styles["icon-btn"]}
            onClick={() => onDelete(data.id)}
            aria-label="Remove certification"
            loading={deletingId === data.id}
            disabled={deletingId !== null || isSaving}
          >
            <TrashOutlineIcon aria-hidden={true} />
            Remove
          </RoundedButton>
        </div>

        <Input
          id={id}
          name="name"
          type="text"
          placeholder="Type the certification name here"
          value={data.name}
          onChange={(e) => onChange({ ...data, name: e.target.value })}
          required
        />
      </div>
    </div>
  );
}
